import React, { useMemo } from "react";
import { Dropdown } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import "./GameFilters.scss";
import ChevronDownIcon from "../../icons/ChevronDownIcon";
import useSubscription from "../../../react-query/queries/use-subscription-status";
import { GamesFilterOptions } from "../../../jotai/atoms/games";

interface GameFilterDetails {
  id: GamesFilterOptions;
  title: string;
  emoji: string;
}

function useGameFilters(): GameFilterDetails[] {
  const { t } = useTranslation("games");

  return useMemo(
    () => [
      {
        id: GamesFilterOptions.POPULAR,
        title: t("popular_games"),
        emoji: "🔥",
      },
      { id: GamesFilterOptions.FAVORITES, title: t("favorites"), emoji: "❤️" },
      { id: GamesFilterOptions.FREE, title: t("free_game"), emoji: "🆓" },
    ],
    [t]
  );
}

interface GameFiltersProps {
  selectedFilterId: GamesFilterOptions;
  handleFilterSelection: (filterId: GamesFilterOptions) => void;
}

export default function GameFilters({
  selectedFilterId,
  handleFilterSelection,
}: GameFiltersProps) {
  const gameFilters = useGameFilters();
  const { isSubscribed } = useSubscription();

  const selectedFilter = useMemo(
    () => gameFilters.find((filter) => filter.id === selectedFilterId),
    [gameFilters, selectedFilterId]
  );

  return (
    <Dropdown className="allow-pointer-events GamesFiltersDropdown">
      <Dropdown.Toggle className="p-0 GamesFiltersToggle">
        <span>{selectedFilter?.emoji}</span>
        {selectedFilter?.title}
        <span className="ms-1">
          <ChevronDownIcon />
        </span>
      </Dropdown.Toggle>

      <Dropdown.Menu className="p-2 GamesFiltersMenu">
        {gameFilters.map(({ id, title, emoji }) => {
          if (isSubscribed && id === GamesFilterOptions.FREE) return null;

          return (
            <Dropdown.Item
              key={title}
              as="button"
              className={`MenuItem ${
                selectedFilterId === id ? "active" : undefined
              }`}
              onClick={() => handleFilterSelection(id)}
            >
              <span>{emoji}</span>
              {title}
            </Dropdown.Item>
          );
        })}
      </Dropdown.Menu>
    </Dropdown>
  );
}
